import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { User } from '../models/user.model';
import { ApiService } from './api.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userSubject = new BehaviorSubject<User | null>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(true);

  user$ = this.userSubject.asObservable();
  token$ = this.tokenSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  constructor(
    private api: ApiService,
    private router: Router,
    private toast: ToastService
  ) {
    this.initializeAuth();
  }

  private getInitialToken(): string | null {
    // Check for UOS token pass-through via URL parameter (iframe embedding)
    const params = new URLSearchParams(window.location.search);
    const uosToken = params.get('uos_token');
    if (uosToken) {
      sessionStorage.setItem('token', uosToken);
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('uos_token');
      window.history.replaceState({}, '', url.toString());
      return uosToken;
    }
    return sessionStorage.getItem('token');
  }

  private initializeAuth(): void {
    const token = this.getInitialToken();
    if (token) {
      this.tokenSubject.next(token);
      this.api.getMe().subscribe({
        next: (res: any) => {
          this.userSubject.next(res.data);
          this.loadingSubject.next(false);
        },
        error: () => {
          sessionStorage.removeItem('token');
          this.tokenSubject.next(null);
          this.loadingSubject.next(false);
        }
      });
    } else {
      this.loadingSubject.next(false);
    }
  }

  login(email: string, password: string): Observable<any> {
    return this.api.login(email, password).pipe(
      tap((res: any) => {
        const { token, refreshToken, user } = res.data;
        sessionStorage.setItem('token', token);
        if (refreshToken) sessionStorage.setItem('refreshToken', refreshToken);
        this.tokenSubject.next(token);
        this.userSubject.next(user);
        
        // Show success toast
        this.toast.success(`Welcome back, ${user.full_name?.split(' ')[0] || 'User'}!`);
        
        // Handle must_change_password redirect
        if (user.must_change_password) {
          // Navigate will be handled after subscription completes
          setTimeout(() => {
            this.router.navigate(['/change-password']);
          }, 100);
        }
      })
    );
  }

  logout(): void {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    this.tokenSubject.next(null);
    this.userSubject.next(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return this.tokenSubject.value;
  }

  // Used by avatar-testing: switch token without full login flow
  setToken(token: string): void {
    sessionStorage.setItem('token', token);
    this.tokenSubject.next(token);
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      this.userSubject.next(payload as any);
    } catch {}
  }

  getCurrentUser(): User | null {
    return this.userSubject.value;
  }

  getUser(): User | null {
    return this.userSubject.value;
  }

  isLoading(): boolean {
    return this.loadingSubject.value;
  }

  // Role computed properties
  get isAdmin(): boolean {
    return this.userSubject.value?.role === 'admin';
  }

  get isOwner(): boolean {
    return this.userSubject.value?.role === 'owner';
  }

  get isSalesManager(): boolean {
    return this.userSubject.value?.role === 'sales_manager';
  }

  get isFieldSalesManager(): boolean {
    return this.userSubject.value?.role === 'field_sales_manager';
  }

  get isFieldSales(): boolean {
    return this.userSubject.value?.role === 'field_sales';
  }

  get isIndoorSales(): boolean {
    return this.userSubject.value?.role === 'indoor_sales';
  }

  get isSalesPricing(): boolean {
    return this.userSubject.value?.role === 'sales_pricing';
  }

  get isFinanceManager(): boolean {
    return this.userSubject.value?.role === 'finance_manager';
  }

  get isFinance(): boolean {
    return ['finance', 'finance_manager'].includes(this.userSubject.value?.role || '');
  }

  get isBilling(): boolean {
    return this.userSubject.value?.role === 'billing';
  }

  get isArCash(): boolean {
    return this.userSubject.value?.role === 'ar_cash';
  }

  get isArDomestic(): boolean {
    return this.userSubject.value?.role === 'ar_domestic';
  }

  get isBankOps(): boolean {
    return this.userSubject.value?.role === 'bank_ops';
  }

  get isProcurement(): boolean {
    return this.userSubject.value?.role === 'procurement';
  }

  get isCsAgent(): boolean {
    return ['cs_agent', 'cs_agent_senior'].includes(this.userSubject.value?.role || '');
  }

  get isCsManager(): boolean {
    return this.userSubject.value?.role === 'cs_manager';
  }

  get isFinanceAny(): boolean {
    return ['admin', 'finance', 'finance_manager', 'billing', 'ar_cash', 'ar_domestic', 'bank_ops', 'procurement']
      .includes(this.userSubject.value?.role || '');
  }

  get isCsRole(): boolean {
    return ['cs_agent', 'cs_agent_senior', 'cs_manager'].includes(this.userSubject.value?.role || '');
  }

  get isAnyManager(): boolean {
    return ['admin', 'sales_manager', 'field_sales_manager'].includes(this.userSubject.value?.role || '');
  }

  get isFieldRole(): boolean {
    return ['field_sales', 'field_sales_manager'].includes(this.userSubject.value?.role || '');
  }

  hasRole(roles: string[]): boolean {
    const role = this.userSubject.value?.role;
    return !!role && roles.includes(role);
  }
}
